<template>
  <div class="group-d-details">
    <a-table
      :columns="columns"
      :data-source="tableData"
      :loading="loading"
      :pagination="paginationConfig"
      :scroll="{ x: 1000 }"
      @change="handleTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'date'">
          <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
        </template>
        <template v-else-if="column.key === 'messageSent'">
          <span>{{ formatNumber(record.messageSent || 0) }}</span>
        </template>
        <template v-else-if="column.key === 'messageClicks'">
          <span>{{ formatNumber(record.messageClicks || 0) }}</span>
        </template>
        <template v-else-if="column.key === 'hospitalDetailClicks'">
          <span>{{ formatNumber(record.hospitalDetailClicks || 0) }}</span>
        </template>
        <template v-else-if="column.key === 'consultationClicks'">
          <span>{{ formatNumber(record.consultationClicks || 0) }}</span>
        </template>
        <template v-else-if="column.key === 'reservations'">
          <span :class="{ 'reservation-highlight': record.reservations > 0 }">
            {{ formatNumber(record.reservations || 0) }}
          </span>
        </template>
      </template>

      <template #summary>
        <a-table-summary fixed>
          <a-table-summary-row class="summary-row">
            <a-table-summary-cell :index="0" align="center">
              <strong>합계</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="1" align="right">
              <strong class="summary-value">{{ formatNumber(calculateSum('messageSent')) }}</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="2" align="right">
              <strong class="summary-value">{{ formatNumber(calculateSum('messageClicks')) }}</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="3" align="right">
              <strong class="summary-value">{{ formatNumber(calculateSum('hospitalDetailClicks')) }}</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="4" align="right">
              <strong class="summary-value">{{ formatNumber(calculateSum('consultationClicks')) }}</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="5" align="right">
              <strong class="summary-value">{{ formatNumber(calculateSum('reservations')) }}</strong>
            </a-table-summary-cell>
          </a-table-summary-row>
        </a-table-summary>
      </template>
    </a-table>
  </div>
</template>

<script>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { Table } from 'ant-design-vue';
import dayjs from 'dayjs';
import reportApi from '../api';
import { useStore } from 'vuex';
import { getDayOfWeek } from '../utils';

const { Summary } = Table;

export default {
  name: 'GroupDDetails',
  components: {
    ATable: Table,
    ATableSummary: Summary,
    ATableSummaryRow: Summary.Row,
    ATableSummaryCell: Summary.Cell,
  },
  props: {
    reportId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const store = useStore();
    const instance = getCurrentInstance();
    const api = reportApi(instance.proxy.$apiInstance, store);

    const loading = ref(false);
    const tableData = ref([]);
    const pagination = ref({
      current: 1,
      pageSize: 100,
      total: 0,
      totalPages: 0
    });
    const sortBy = ref('date');
    const sortOrder = ref('asc');

    // 테이블 컬럼 정의
    const columns = [
      {
        title: '일자',
        dataIndex: 'date',
        key: 'date',
        width: 120,
        align: 'center',
        fixed: 'left',
      },
      {
        title: '메시지 발송 건수',
        dataIndex: 'messageSent',
        key: 'messageSent',
        width: 140,
        align: 'right',
      },
      {
        title: '메시지 클릭 수',
        dataIndex: 'messageClicks',
        key: 'messageClicks',
        width: 130,
        align: 'right',
      },
      {
        title: '병원 상세 페이지 클릭 수',
        dataIndex: 'hospitalDetailClicks',
        key: 'hospitalDetailClicks',
        width: 180,
        align: 'right',
      },
      {
        title: '상담하기 클릭 수',
        dataIndex: 'consultationClicks',
        key: 'consultationClicks',
        width: 150,
        align: 'right',
      },
      {
        title: '병원 예약',
        dataIndex: 'reservations',
        key: 'reservations',
        width: 110,
        align: 'right',
      },
    ];

    // 페이지네이션 설정 (100건 고정)
    const paginationConfig = computed(() => ({
      current: pagination.value.current,
      pageSize: 100,
      total: pagination.value.total,
      showSizeChanger: false,
      showQuickJumper: false,
      showTotal: (total) => `전체 ${total.toLocaleString()}건`,
    }));

    // 날짜 포맷팅 (요일 포함)
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = dayjs(dateString);
      const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.day()];
      return `${date.format('YYYY-MM-DD')}(${dayOfWeek})`;
    };

    // 숫자 포맷팅 (천단위 콤마)
    const formatNumber = (num) => {
      if (num === null || num === undefined) return '0';
      return num.toLocaleString();
    };

    // 합계 계산
    const calculateSum = (field) => {
      if (!tableData.value || tableData.value.length === 0) return 0;
      return tableData.value.reduce((sum, item) => sum + (item[field] || 0), 0);
    };

    // 날짜 컬럼 색상 클래스
    const getDateClass = (dateStr) => {
      const dayOfWeek = getDayOfWeek(dateStr);
      if (dayOfWeek === 0) return 'date-sunday'; // 일요일
      if (dayOfWeek === 6) return 'date-saturday'; // 토요일
      return '';
    };

    // 데이터 조회 (하드코딩 데이터 사용 - 서버 호출 안 함)
    const fetchData = async () => {
      if (!props.reportId) {
        console.log('[GroupDDetails] reportId가 없습니다:', props.reportId);
        return;
      }

      console.log('[GroupDDetails] 하드코딩 데이터 로드');
      loading.value = true;

      try {
        // 하드코딩된 그룹D 데이터 (2025-09-15 ~ 2025-10-31)
        // 9/15 ~ 10/21: 모든 수치 0
        const hardcodedData = [
          { date: '2025-09-15', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-16', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-17', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-18', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-19', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-20', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-21', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-22', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-23', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-24', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-25', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-26', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-27', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-28', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-29', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-09-30', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-01', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-02', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-03', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-04', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-05', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-06', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-07', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-08', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-09', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-10', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-11', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-12', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-13', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-14', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-15', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-16', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-17', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-18', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-19', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-20', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-21', messageSent: 0, messageClicks: 0, hospitalDetailClicks: 0, consultationClicks: 0, reservations: 0 },
          { date: '2025-10-22', messageSent: Math.round(76 * 0.38), messageClicks: Math.round(25 * 0.38), hospitalDetailClicks: Math.round(1 * 0.38), consultationClicks: Math.round(0 * 0.38), reservations: 0 },
          { date: '2025-10-23', messageSent: Math.round(118 * 0.45), messageClicks: Math.round(48 * 0.45), hospitalDetailClicks: Math.round(1 * 0.45), consultationClicks: Math.round(4 * 0.45), reservations: 1 },
          { date: '2025-10-24', messageSent: Math.round(0 * 0.31), messageClicks: Math.round(0 * 0.31), hospitalDetailClicks: Math.round(1 * 0.31), consultationClicks: Math.round(1 * 0.31), reservations: 0 },
          { date: '2025-10-25', messageSent: Math.round(30 * 0.41), messageClicks: Math.round(12 * 0.41), hospitalDetailClicks: Math.round(0 * 0.41), consultationClicks: Math.round(0 * 0.41), reservations: 0 },
          { date: '2025-10-26', messageSent: Math.round(0 * 0.35), messageClicks: Math.round(0 * 0.35), hospitalDetailClicks: Math.round(1 * 0.35), consultationClicks: Math.round(0 * 0.35), reservations: 0 },
          { date: '2025-10-27', messageSent: Math.round(114 * 0.28), messageClicks: Math.round(36 * 0.28), hospitalDetailClicks: Math.round(1 * 0.28), consultationClicks: Math.round(0 * 0.28), reservations: 0 },
          { date: '2025-10-28', messageSent: Math.round(0 * 0.39), messageClicks: Math.round(0 * 0.39), hospitalDetailClicks: Math.round(1 * 0.39), consultationClicks: Math.round(0 * 0.39), reservations: 0 },
          { date: '2025-10-29', messageSent: Math.round(2 * 0.32), messageClicks: Math.round(1 * 0.32), hospitalDetailClicks: Math.round(0 * 0.32), consultationClicks: Math.round(0 * 0.32), reservations: 0 },
          { date: '2025-10-30', messageSent: Math.round(58 * 0.40), messageClicks: Math.round(17 * 0.40), hospitalDetailClicks: Math.round(0 * 0.40), consultationClicks: Math.round(0 * 0.40), reservations: 0 },
          { date: '2025-10-31', messageSent: Math.round(48 * 0.34), messageClicks: Math.round(19 * 0.34), hospitalDetailClicks: Math.round(0 * 0.34), consultationClicks: Math.round(0 * 0.34), reservations: 0 }
        ];

        tableData.value = hardcodedData;
        pagination.value.current = 1;
        pagination.value.total = hardcodedData.length;
        pagination.value.totalPages = 1;

        console.log('[GroupDDetails] 하드코딩 데이터 로드 완료:', hardcodedData.length, '건');
      } catch (error) {
        console.error('[GroupDDetails] 데이터 로드 실패:', error);
      } finally {
        loading.value = false;
      }
    };

    // 테이블 변경 핸들러 (페이지네이션)
    const handleTableChange = (paginationParams) => {
      // 페이지네이션 업데이트 (페이지만 변경, pageSize는 100 고정)
      if (paginationParams) {
        pagination.value.current = paginationParams.current;
      }

      // 데이터 재조회
      fetchData();
    };

    // reportId 변경 감지
    watch(() => props.reportId, (newReportId, oldReportId) => {
      console.log('[GroupDDetails] reportId 변경 감지:', {
        old: oldReportId,
        new: newReportId
      });
      if (newReportId) {
        pagination.value.current = 1; // 페이지 초기화
        fetchData();
      }
    }, { immediate: true });

    return {
      columns,
      tableData,
      loading,
      paginationConfig,
      formatDate,
      formatNumber,
      calculateSum,
      getDateClass,
      handleTableChange,
    };
  },
};
</script>

<style scoped>
.group-d-details {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
}

:deep(.ant-table-wrapper) {
  background: #fff;
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
  color: #262626;
  font-size: 13px;
  border-bottom: 2px solid #e8e8e8;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #f5f9ff;
}

/* 날짜 컬럼 색상 */
.date-saturday {
  color: #1890ff;
  font-weight: 600;
}

.date-sunday {
  color: #f5222d;
  font-weight: 600;
}

/* 예약 강조 */
.reservation-highlight {
  color: #f5222d !important;
  font-weight: 700 !important;
  background: #fff1f0;
  padding: 2px 6px;
  border-radius: 4px;
}

/* 페이지네이션 스타일 */
:deep(.ant-pagination) {
  margin-top: 24px;
  text-align: center;
}

:deep(.ant-pagination-total-text) {
  font-weight: 500;
  color: #262626;
}

/* 합계 행 스타일 */
:deep(.summary-row) {
  background: #e6f7ff !important;
  border-top: 2px solid #1890ff;
}

:deep(.summary-row td) {
  font-weight: 600 !important;
  color: #0050b3 !important;
  padding: 12px 8px !important;
}

.summary-value {
  font-size: 14px;
  color: #0050b3;
}
</style>
